/* =============================================================
   useStudentContext.js — Resolves the active student identity
   -------------------------------------------------------------
   This hook is the single source of truth for "who is the
   student I'm looking at" across the entire student portal.

   Two modes:

     Admin Preview mode  (?as=<studentUUID> in the URL)
       An IO manager visits /student?as=<uuid> to test the
       student portal on behalf of a specific student.
       The hook reads the UUID from the URL search params,
       fetches the student record, and returns isPreview = true.

     Real Student mode  (production — student is logged in)
       The logged-in user has role = 'student'. The hook finds
       their own students row by matching auth_user_id to the
       Supabase auth UID. isPreview = false.

   Cache strategy (module-level Map):
     _studentCache survives React component unmount/remount and
     Supabase token refreshes (which temporarily set user → null
     and back). On every mount the hook returns cached data
     immediately (no skeleton flash), then silently re-fetches
     in the background to stay fresh.

   Usage:
     const { studentId, student, loading, isPreview, exitUrl }
       = useStudentContext();

   Returns:
     studentId  — UUID of the student (null while loading)
     student    — full students row + partner_universities join
     loading    — true ONLY on the very first fetch (no cache yet)
     isPreview  — true when an admin is in preview mode
     exitUrl    — where to navigate when exiting preview mode
                  (comes from the ?from= param set by the admin
                  profile page, falls back to /dashboard)
   ============================================================= */

import { useState, useEffect } from 'react';
import { useSearchParams }     from 'react-router-dom';
import { supabase }            from '../lib/supabase';
import { useAuth }             from '../context/AuthContext';


/* ─── MODULE-LEVEL CACHE ─────────────────────────────────────── */
// Survives React unmount/remount and Supabase token refresh.
// Key: "preview:<uuid>" or "student:<authUserId>"
const _studentCache = new Map();


export function useStudentContext() {
  const { user }   = useAuth();
  const [params]   = useSearchParams();

  // Admin preview: ?as=<studentUUID> only respected when the user is an admin.
  const previewId = user?.role === 'admin' ? params.get('as') : null;
  const isPreview = !!previewId;

  // Where to go when the admin clicks "Exit Preview".
  const exitUrl = params.get('from') ?? '/dashboard';

  // Stable cache key for this identity
  const cacheKey = previewId
    ? `preview:${previewId}`
    : user?.role === 'student' ? `student:${user.id}`
    : null;

  const _cached = cacheKey ? _studentCache.get(cacheKey) : undefined;

  // Initialize from cache immediately — no skeleton flash on re-navigation
  const [student, setStudent] = useState(_cached ?? null);
  const [loading, setLoading] = useState(_cached === undefined);


  useEffect(() => {
    if (!user) { setLoading(false); return; }
    if (!cacheKey) { setLoading(false); return; }

    async function fetchStudent() {
      // Show spinner only if we have no cached data for this student
      if (!_studentCache.has(cacheKey)) setLoading(true);

      let data = null;

      if (previewId) {
        // ── Admin preview: fetch by UUID from URL ──────────────
        ({ data } = await supabase
          .from('students')
          .select('*, partner_universities(name, country, abbreviation, exchange_period, linked_university_id, universities!linked_university_id(key_dates)), universities(name, country, exchange_period, key_dates)')
          .eq('id', previewId)
          .single());

      } else if (user.role === 'student') {
        // ── Real student: find their own record ────────────────
        ({ data } = await supabase
          .from('students')
          .select('*, partner_universities(name, country, abbreviation, exchange_period, linked_university_id, universities!linked_university_id(key_dates)), universities(name, country, exchange_period, key_dates)')
          .eq('auth_user_id', user.id)
          .maybeSingle());
      }

      if (data) {
        setStudent(data);
        _studentCache.set(cacheKey, data);
      } else {
        setStudent(null);
      }

      setLoading(false);
    }

    fetchStudent();
  }, [previewId, user?.id, user?.role, cacheKey]); // eslint-disable-line react-hooks/exhaustive-deps


  return {
    studentId: student?.id   ?? null,
    student,
    loading,
    isPreview,
    exitUrl,
  };
}
